import * as S from './styles.ts';
import {
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePopper } from 'react-popper';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Placement } from '@popperjs/core';
import { PopupProvider } from './context.tsx';
import { usePathname } from 'next/navigation';
import { useFrame } from 'react-frame-component';
import useBreakpoint from '@/lib/helpers/hooks/useBreakpoint.ts';
import useClickOutside from '@/lib/helpers/hooks/useClickOutside.ts';

export interface IPopupProps {
  children?: ReactNode | null;
  popupContent: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: Placement;
  align?: 'start' | 'end';
  unstyled?: boolean;
  unstyledTrigger?: boolean;
  offset?: [x: number, y: number];
  zIndex?: number;
  width?: number;
  triggerProps?: typeof HTMLButtonElement;
  minFullWidth?: boolean;
  triggeredByHover?: boolean;
}

const PopupContent = ({
  setPopperElement,
  styles,
  attributes,
  popupContent,
  usedIsOpen,
  popupContentRef,
  handleMouseOver,
  handleMouseOut,
  isSm,
  unstyled,
  zIndex,
  width,
}: {
  setPopperElement: any;
  styles: any;
  attributes: any;
  popupContent: ReactNode;
  usedIsOpen: boolean;
  popupContentRef: any;
  handleMouseOver: () => void;
  handleMouseOut: () => void;
  isSm?: boolean;
  unstyled?: boolean;
  zIndex?: number;
  width?: number;
}) => (
  <AnimatePresence>
    {usedIsOpen && (
      <>
        {isSm ? (
          <S.MobilePopupContentWrapper
            data-is-popup={true}
            as={motion.div}
            initial={{
              opacity: 0,
              bottom: -40,
            }}
            animate={{
              opacity: 1,
              bottom: 0,
            }}
            exit={{
              opacity: 0,
              bottom: -40,
            }}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            <S.PopupContent ref={popupContentRef} $unstyled={unstyled}>
              {popupContent}
            </S.PopupContent>
          </S.MobilePopupContentWrapper>
        ) : (
          <S.PopupContentWrapper
            data-is-popup={true}
            data-popper-component={true}
            as={motion.div}
            initial={{
              opacity: 0,
              marginTop: 15,
            }}
            animate={{
              opacity: 1,
              marginTop: 0,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
              type: 'easeOut',
            }}
            ref={setPopperElement as any}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            style={{
              ...styles.popper,
              zIndex,
            }}
            {...attributes.popper}
          >
            <S.PopupContent
              ref={popupContentRef}
              $unstyled={unstyled}
              style={{
                width,
              }}
            >
              {popupContent}
            </S.PopupContent>
          </S.PopupContentWrapper>
        )}
      </>
    )}
  </AnimatePresence>
);

const Popup = ({
  children,
  popupContent,
  isOpen,
  onOpenChange,
  placement,
  align,
  unstyled = false,
  unstyledTrigger,
  offset,
  zIndex,
  width,
  triggerProps,
  minFullWidth,
  triggeredByHover,
}: IPopupProps) => {
  const path = usePathname();
  const frame = useFrame();

  const timerRef = useRef<any>(null);

  const [mounted, setMounted] = useState(false);
  const smBp = useBreakpoint('sm').check;

  const isSm = !smBp;

  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [defaultIsOpen, setDefaultIsOpen] = useState(false);

  const modifiers: any = useMemo(
    () => [
      {
        name: 'offset',
        options: {
          offset: offset ? offset : [0, 5],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: true,
          altAxis: true,
          boundary: mounted
            ? (document.getElementsByTagName('body')[0] as HTMLElement)
            : undefined,
          padding: 5,
        },
      },
      ...(minFullWidth
        ? [
            {
              name: 'minFullWidth',
              enabled: true,
              fn: ({ state }: any) => {
                state.styles.popper.minWidth = `${state.rects.reference.width}px`;
              },
              phase: 'beforeWrite',
              requires: ['computeStyles'],
            },
          ]
        : []),
    ],
    [mounted]
  );

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      modifiers,
      placement: [placement ? placement : 'top', align ? align : null]
        .filter((x) => x !== null)
        .join('-') as Placement,
    }
  );

  const hasIsOpen = isOpen !== undefined;

  const usedIsOpen = hasIsOpen ? isOpen : defaultIsOpen;

  const toggleIsOpen = (val: boolean) => {
    if (onOpenChange) {
      onOpenChange(val);
    }
    setDefaultIsOpen(val);
  };

  const popupContentRef = useRef<HTMLDivElement>(null);

  const handleMouseOut = () => {
    if (triggeredByHover && usedIsOpen) {
      timerRef.current = setTimeout(() => {
        toggleIsOpen(false);
      }, 100);
    }
  };

  const cancelMouseOut = () => {
    clearTimeout(timerRef.current);
  };

  const handleMouseOver = () => {
    if (triggeredByHover) {
      cancelMouseOut();
      toggleIsOpen(true);
      clearTimeout(timerRef.current);
    }
  };

  useClickOutside(
    popupContentRef,
    (e: any) => {
      const target = e.target;

      const mouseOverTarget = e.relatedTarget;

      const isPopupOpenButton = !!target?.closest(
        '[data-is-popup-open="true"]'
      );

      if (isPopupOpenButton && toggleIsOpen) {
        e.stopPropagation();
        toggleIsOpen(false);
        return;
      }

      //check if clicked element is part of another popup
      const isPopup =
        !!target?.closest('[data-popper-component]') ||
        !!mouseOverTarget?.closest('[data-popper-component]');

      //check if clicked element is active editor component
      const isActiveElement =
        !!target?.closest('[data-is-active]') ||
        !!mouseOverTarget?.closest('[data-is-active]');

      // if clicked element is not another popup, close it
      if (!isPopup && !isActiveElement) {
        if (toggleIsOpen) {
          e.stopPropagation();
          toggleIsOpen(false);
        }
      }
    },
    [usedIsOpen]
  );

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
    return () => {
      clearTimeout(timerRef.current);
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    toggleIsOpen(false);
  }, [path]);

  const isInIframe = frame?.document?.URL === 'about:srcdoc';

  if (!mounted) return;

  const popupContentProps = {
    setPopperElement,
    styles,
    attributes,
    popupContent,
    usedIsOpen,
    popupContentRef,
    handleMouseOver,
    handleMouseOut,
    isSm,
    unstyled,
    zIndex,
    width,
  };

  return (
    <PopupProvider isOpen={usedIsOpen} toggleIsOpen={toggleIsOpen}>
      <Fragment>
        <S.PopupBtn
          data-is-popup-open={usedIsOpen}
          ref={setReferenceElement as any}
          onClick={(e) => {
            if (!triggeredByHover) {
              e.stopPropagation();
              toggleIsOpen(!usedIsOpen);
            }
          }}
          $unstyled={unstyledTrigger}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          tabIndex={0}
          onKeyDown={(e) => {
            const key = e.key;
            const activationKeys = ['Enter', ' '];
            if (!activationKeys.includes(key)) return;
            e.preventDefault();
            toggleIsOpen(!usedIsOpen);
          }}
          {...triggerProps}
        >
          {children}
        </S.PopupBtn>
        {!isInIframe ? (
          createPortal(
            <PopupContent {...popupContentProps} />,
            document.querySelector('body') as any
          )
        ) : (
          <PopupContent {...popupContentProps} />
        )}
      </Fragment>
    </PopupProvider>
  );
};

export default Popup;
